import React, { useEffect, useState } from "react";
import Button from "../Button";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { useNavigate, useParams } from "react-router-dom";
import {
  setCoach,
  setFinalAmountPaid,
} from "src/redux/slices/appointmentsSlice";
import { getCoachListRequest } from "src/redux/slices/appointmentsSlice";
import { CoachData, HealthCoachType } from "src/types/appointment.types";
import { Users } from "lucide-react";
import Badge from "src/components/common/Badge";
import {
  HEALTH_COACH_TYPES,
  IS_HEALTH_COACH_ENABLED,
  BOOK_APPOINTMENT_PAYMENT_STATUS,
  APPOINTMENT_SLOT_BOOKING_STATUS,
} from "src/constants/common";
import CoachProfile from "../avatar/CoachProfile";
import { useCheckForLatestConsultation } from "src/hooks/useCheckConsultation";
import { toast } from "sonner";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

const CoachList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const patientDetails = useAppSelector((state) => state.auth.patient.data);
  const { coachList } = useAppSelector((state) => state.appointments);
  const [isPaidCoachLoading, setIsPaidCoachLoading] = useState<boolean>(false);
  const { checkForLatestConsultation } = useCheckForLatestConsultation();

  useEffect(() => {
    fetchCoachList();
  }, []);

  const fetchCoachList = () => {
    return new Promise((resolve, reject) => {
      dispatch(
        getCoachListRequest({
          resolve,
          reject,
          payload: {
            patient_plan_rel_id:
              patientDetails?.active_plan_details?.patient_plan_rel_id,
          },
        })
      );
    });
  };

  const canShowHealthCoach = (
    key: HealthCoachType
  ): { canShow: boolean; isPaid: boolean } | undefined => {
    const allCoachData = coachList.data;
    // Condition 1: Ignoring this condition for now as we are not using it, not in use/operational considering backend flow.
    // const featureMap: Record<HealthCoachType, keyof HealthCoachFeatureList> = {
    //   nutritionist: "nutri_coach",
    //   physiotherapist: "physio_coach",
    //   psychoanalyst: "success_coach",
    // };
    // return !!patientDetails?.health_coach_feature_list?.[
    //   featureMap[key] as keyof typeof patientDetails.health_coach_feature_list
    // ];

    // Condition 2: Check if the coach is present or not in allCoachData.
    const isCoachPresent = allCoachData?.[key] ? true : false;
    if (isCoachPresent) {
      // check if the coach is paid or free
      const isPaid =
        allCoachData?.[`is_${key}_enable`] === IS_HEALTH_COACH_ENABLED.NO;
      // if paid, then check for plan master id and plan package duration rel id
      if (isPaid) {
        const planMasterId = allCoachData?.[key]?.[`plan_master_id`];
        const planPackageDurationRelId =
          allCoachData?.[key]?.[`plan_package_duration_rel_id`];
        if (planMasterId && planPackageDurationRelId) {
          return { canShow: true, isPaid: true };
        }
        return { canShow: false, isPaid: true };
      }
      // coach is free, no need to check for plan master id and plan package duration rel id
      return { canShow: true, isPaid: false };
    }
    return { canShow: false, isPaid: false };

    // Some other conditions might come up in future, for example checking the availability of the coach.
    // But for now, we are only checking if the coach is present or not in the coach list data.
  };

  const navigateToSlotSelection = (key: HealthCoachType) => {
    navigate(`/app/${supportProgramKey}/book-consultation/select-slot/${key}`);
  };

  const navigateToPayment = (key: HealthCoachType) => {
    navigate(`/app/${supportProgramKey}/book-consultation/payment/${key}`);
  };

  const handleBookSpecialist = async (
    key: HealthCoachType,
    isPaid: boolean
  ) => {
    // Event: BOOK_COACH_CLICKED
    analytics.trackEvent(`${key}_${EVENT_NAMES.BOOK_COACH_CLICKED}`, {
      coach_type: key,
      screen_name: SCREEN_NAMES.BOOK_CONSULTATION_SCREEN,
      support_program_key: supportProgramKey,
    });
    const coachData = coachList.data?.[key];
    const healthCoachId = coachData?.health_coach_id;
    if (!healthCoachId) {
      return;
    }
    const updatedCoach = { ...coachData, isPaid };
    dispatch(setCoach(updatedCoach)); // store it globally
    if (isPaid) {
      setIsPaidCoachLoading(true);
      const latestConsulatationPlan = await checkForLatestConsultation(
        coachData?.plan_master_id,
        coachData?.plan_package_duration_rel_id,
        // isPaid,
        // key,
        setIsPaidCoachLoading
      );

      const { payment_status, slot_booking_process, purchase_amount } =
        latestConsulatationPlan ?? {};

      if (
        payment_status === BOOK_APPOINTMENT_PAYMENT_STATUS.COMPLETED &&
        slot_booking_process === APPOINTMENT_SLOT_BOOKING_STATUS.PENDING &&
        purchase_amount
      ) {
        toast.info(
          "You have done the payment successfully! Please select your appointment slot."
        );

        dispatch(setFinalAmountPaid(Number(purchase_amount)));
        navigateToSlotSelection(key);
      } else {
        navigateToPayment(key);
      }
    } else {
      navigateToSlotSelection(key);
    }
  };

  const getCustomCoachCard = (
    coachData: CoachData,
    coachKey: HealthCoachType,
    isPaid: boolean,
    onArrowClick: () => void
  ) => {
    return (
      <div key={coachData.health_coach_id} className="flex-shrink-0 w-36">
        <div className="shadow-sm border-0 bg-white rounded-xl overflow-hidden h-44">
          <div className="p-3 h-full flex flex-col justify-between">
            <div className="flex-1 flex flex-col items-center justify-center">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 overflow-hidden"
                style={{ backgroundColor: "var(--icon-bg-primary)" }}
              >
                <CoachProfile coachKey={coachKey} />
              </div>
              <h3 className="font-semibold text-sm text-gray-800 mb-2 text-center truncate w-full">
                {coachData?.role || `Coach (${coachKey})`}
              </h3>
              <div className="mb-3">
                {isPaid ? (
                  <span className="text-base font-bold">
                    ₹ {coachData?.price || coachData?.mrp}
                  </span>
                ) : (
                  <Badge type="warning">Free</Badge>
                )}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => onArrowClick()}
              loading={isPaidCoachLoading}
              disabled={isPaidCoachLoading}
            >
              Book
            </Button>
          </div>
        </div>
      </div>
    );
  };

  //   const getDateTimeCard = () => (
  //     <div className="p-3">
  //       <div className="my-4">
  //         <div className="flex">
  //           <div className="mr-2">
  //             <CalendarIcon className="h-5 w-5" />
  //           </div>
  //           <div className="text-sm">
  //             {/* {moment(queryParams["selected_date"]).format(
  //                   "dddd, DD MMMM, YYYY"
  //                 )} */}
  //             Monday, 24 December,2024
  //           </div>
  //         </div>
  //         <div className="flex mt-2">
  //           <div className="mr-2">
  //             <ClockIcon className="h-5 w-5" />
  //           </div>
  //           <div className="text-sm">
  //             {/* {queryParams["period"]}, {queryParams["time_slot"]} */}
  //             Evening, 08:00 PM - 10:00 PM
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );

  const showCoachCard = (key: HealthCoachType, isPaid: boolean) => {
    const healthCoachData = coachList.data?.[key];
    if (!healthCoachData) return null;
    return getCustomCoachCard(healthCoachData, key, isPaid, () =>
      handleBookSpecialist(key, isPaid)
    );
  };

  const canShowNutritionistCard = canShowHealthCoach(
    HEALTH_COACH_TYPES.NUTRITIONIST
  );
  const canShowPhysiotherapistCard = canShowHealthCoach(
    HEALTH_COACH_TYPES.PHYSIOTHERAPIST
  );
  const canShowPsychoanalystCard = canShowHealthCoach(
    HEALTH_COACH_TYPES.PSYCHOANALYST
  );

  const nutritionistCard = canShowNutritionistCard?.canShow
    ? showCoachCard(
        HEALTH_COACH_TYPES.NUTRITIONIST,
        canShowNutritionistCard.isPaid
      )
    : null;
  const physiotherapistCard = canShowPhysiotherapistCard?.canShow
    ? showCoachCard(
        HEALTH_COACH_TYPES.PHYSIOTHERAPIST,
        canShowPhysiotherapistCard.isPaid
      )
    : null;
  const psychoanalystCard = canShowPsychoanalystCard?.canShow
    ? showCoachCard(
        HEALTH_COACH_TYPES.PSYCHOANALYST,
        canShowPsychoanalystCard.isPaid
      )
    : null;

  const noCoachAvailable: boolean =
    !nutritionistCard && !physiotherapistCard && !psychoanalystCard;

  return (
    <div className="pb-2">
      <h2 className="text-lg font-semibold text-gray-800 pb-4">Coaches List</h2>
      {noCoachAvailable ? (
        <div className="flex flex-col items-center justify-center gap-3 h-full col-span-2">
          <div className="bg-gray-200 rounded-full p-3">
            <Users className="w-6 h-6 text-gray-500" />
          </div>
          <p className="text-sm text-gray-500">No coach available</p>
        </div>
      ) : (
        <div className="flex space-x-4 overflow-x-auto">
          {nutritionistCard}
          {physiotherapistCard}
          {psychoanalystCard}
        </div>
      )}
    </div>
  );
};

export default CoachList;
